import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import {
  getFeatureFromPlan,
  OrderProductPreview,
  RecurlyAddOn,
} from '../../../../models';
import { formatCurrency } from '../../../../components';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';
import { PACKAGE_SIZE } from './BonusSeatTreatment';

const useStyles = makeStyles(theme => ({
  starterChip: {
    backgroundColor: '#F59E0B',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 10,
    height: 20,
    marginLeft: theme.spacing(1),
    borderRadius: theme.spacing(1),
  },
  pricingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fullAmount: {
    fontWeight: 700,
    fontSize: 16,
    color: '#22283c',
    textDecoration: 'line-through',
  },
  pricingAmount: {
    fontWeight: 700,
    fontSize: 16,
    color: '#0F8461',
  },
}));

const PriceRow = (
  {
    id,
    showStarterChip,
    quantity,
    unit,
    displayName,
    subTotal,
    total,
    isAnnual,
    isDiscount,
  }: {
    id: string,
    showStarterChip?: boolean;
    quantity: number;
    unit?: string;
    displayName?: any;
    subTotal: number,
    total: number,
    isAnnual: boolean,
    isDiscount?: boolean,
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const effectiveUnit = quantity > 0 ? total / quantity : 0;
  const effectiveMonthlyUnit = isAnnual ? effectiveUnit / 12 : effectiveUnit;

  return (
    <Box
      id={`price-row-${id}`}
      key={`price-row-${id}`}
      py={2}
    >
      <Box
        className={classes.pricingRow}
      >
        <Box
          id={`price-row-description-${id}`}
          key={`price-row-description-${id}`}
          display="flex"
          alignItems="center"
          pb={1}
        >
          <Typography fontWeight={700} fontSize={16} color="#22283c">
            <FormattedMessage
              id="page.plan.v2.true.price.summary"
              values={{
                quantity,
                unit,
                displayName: displayName
                  ? `x ${displayName}`
                  : '',
              }}
            />
          </Typography>
          {showStarterChip && (
            <Tooltip
              arrow
              title={intl.formatMessage({ id: 'page.plan.v2.package.offer.tooltip' })}
            >
              <Chip
                label={intl.formatMessage({ id: 'page.plan.v2.starter.chip' })}
                size="small"
                className={classes.starterChip}
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          )}
        </Box>
        {isDiscount && (
          <Typography className={classes.fullAmount}>
            {formatCurrency(intl, subTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
          </Typography>
        )}
      </Box>
      <Box
        className={classes.pricingRow}
      >
        <Typography className={classes.pricingAmount}>
          <FormattedMessage
            id="page.plan.v2.per.seat.month"
            values={{
              unit: formatCurrency(
                intl,
                effectiveMonthlyUnit,
                DEFAULT_CURRENCY_FORMAT_OPTIONS,
              ),
            }}
          />
        </Typography>
        <Typography className={classes.pricingAmount}>
          {formatCurrency(intl, total, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
        </Typography>
      </Box>
    </Box>
  );
};

export const PriceRows = (
  { isAnnual, productsPreview }: {
    isAnnual: boolean;
    productsPreview: OrderProductPreview;
  },
) => {
  const intl = useIntl();
  const productFeature = getFeatureFromPlan(productsPreview.productCode)
    ?? productsPreview.feature;
  const truePricing = productsPreview.lineItem.find(
    it => getFeatureFromPlan(it.addOnCode) === productFeature,
  ) ?? productsPreview.lineItem.find(
    it => it.addOnCode === productsPreview.productCode,
  );
  const otherLineItems = productsPreview.lineItem.filter(
    it => Object.values(RecurlyAddOn).some(addOn => addOn === it.addOnCode)
      && it.addOnCode !== RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
  );
  const discountPrice = (truePricing?.unitAmount ?? 0) * productsPreview.discountQuantity
    - productsPreview.discountAmount;
  const fullPriceQuantity = productsPreview.discountAmount > 0
    ? (productsPreview.newQuantity - productsPreview.discountQuantity)
    : productsPreview.newQuantity;
  const fullPriceTotal = productsPreview.discountAmount > 0
    ? (truePricing?.subtotal ?? 0) - (discountPrice + productsPreview.discountAmount)
    : (truePricing?.subtotal ?? 0);

  return (
    <Box
      id={`pricing-row-${productsPreview.feature.toLowerCase()}`}
      key={`pricing-row-${productsPreview.feature.toLowerCase()}`}
    >
      {(productsPreview.discountQuantity > 0 && productsPreview.discountAmount > 0) && (
        <>
          <PriceRow
            id="true-discount"
            key={`price-row${productsPreview.feature.toLowerCase()}-discount`}
            showStarterChip={isAnnual && productsPreview.newQuantity >= PACKAGE_SIZE}
            quantity={productsPreview.discountQuantity}
            unit={intl.formatMessage({ id: 'page.plan.v2.seats.unit' })}
            subTotal={productsPreview.discountQuantity * productsPreview.unitAmount}
            total={discountPrice}
            isAnnual={isAnnual}
            isDiscount
          />
          <Divider sx={{ borderStyle: 'dashed' }} />
        </>
      )}
      {fullPriceQuantity > 0 && (
        <>
          <PriceRow
            id="true-full-price"
            key={`price-row${productsPreview.feature.toLowerCase()}-full`}
            quantity={fullPriceQuantity}
            unit={intl.formatMessage({ id: 'page.plan.v2.seats.unit' })}
            subTotal={productsPreview.subTotal}
            total={fullPriceTotal}
            isAnnual={isAnnual}
          />
          <Divider sx={{ borderStyle: 'dashed' }} />
        </>
      )}
      {otherLineItems.map(lineItem => (
        <>
          <PriceRow
            id={lineItem.addOnCode}
            key={`price-row-${lineItem.addOnCode}`}
            quantity={lineItem.quantity}
            displayName={intl.formatMessage({ id: `addon.${lineItem.addOnCode}.display.name` })}
            subTotal={lineItem.subtotal}
            total={lineItem.subtotal}
            isAnnual={isAnnual}
          />
          <Divider sx={{ borderStyle: 'dashed' }} />
        </>
      ))}
    </Box>
  );
};
